import React from 'react';
import { Box, TextField } from '@mui/material';
import type { InlineEditorProps } from 'components/common/datagrid/CellValue';

const DEFAULT_PICKER_SIZE = 32;

const colorFieldSx = (hasError: boolean) => ({
  '& .MuiInputBase-root': {
    border: hasError ? '1px solid' : undefined,
    borderColor: hasError ? 'error.main' : undefined,
    borderRadius: 1,
    px: 0.5,
  },
});

export const isValidHexColor = (value: string) => /^#[0-9A-Fa-f]{6}$/.test(value);

export const isPartialHexColor = (value: string) => /^#[0-9A-Fa-f]{0,6}$/.test(value);

const componentToHex = (value: number) => value.toString(16).padStart(2, '0');

const parseRgbColor = (value: string): { r: number; g: number; b: number } | null => {
  const match = value
    .trim()
    .match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+\s*)?\)$/i);

  if (!match) {
    return null;
  }

  const channels = [Number(match[1]), Number(match[2]), Number(match[3])];

  if (channels.some(channel => Number.isNaN(channel) || channel < 0 || channel > 255)) {
    return null;
  }

  return { r: channels[0], g: channels[1], b: channels[2] };
};

const expandShortHexColor = (value: string): string | null => {
  const match = value.match(/^#([0-9A-Fa-f]{3})$/i);

  if (!match) {
    return null;
  }

  const [r, g, b] = match[1].split('');
  return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
};

export const normalizeColorToHex = (value: string, fallback = '#000000'): string => {
  const trimmed = value.trim();

  if (isValidHexColor(trimmed)) {
    return trimmed.toLowerCase();
  }

  const expanded = expandShortHexColor(trimmed);

  if (expanded && isValidHexColor(expanded)) {
    return expanded;
  }

  const rgb = parseRgbColor(trimmed);

  if (rgb) {
    return `#${componentToHex(rgb.r)}${componentToHex(rgb.g)}${componentToHex(rgb.b)}`;
  }

  return fallback.toLowerCase();
};

const resolvePickerColor = (value: string, fallback: string): string => {
  const normalized = normalizeColorToHex(value, '');

  if (normalized && isValidHexColor(normalized)) {
    return normalized;
  }

  return fallback.toLowerCase();
};

export const RenderColorValue = (color: string) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: '5px',
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: color,
      }}
    />
    {color}
  </Box>
);

type ColorFieldProps = {
  value: string;
  onChange: (value: string) => void;
  onPickerChange?: (value: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  hexInputRef?: React.Ref<HTMLInputElement>;
  onHexKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onHexBlur?: React.FocusEventHandler<HTMLInputElement>;
  onColorInputCommit?: (value: string) => void;
  gap?: number;
  minWidth?: number | string;
};

const ColorField = ({
  value,
  onChange,
  onPickerChange,
  disabled,
  hasError,
  hexInputRef,
  onHexKeyDown,
  onHexBlur,
  onColorInputCommit,
  gap = 2,
  minWidth,
}: ColorFieldProps) => {
  const fieldWrapperRef = React.useRef<HTMLDivElement>(null);
  const colorInputRef = React.useRef<HTMLInputElement>(null);
  const [pickerSize, setPickerSize] = React.useState(DEFAULT_PICKER_SIZE);
  const lastValidColorRef = React.useRef(resolvePickerColor(value, '#000000'));

  const measureFieldHeight = React.useCallback(() => {
    const root = fieldWrapperRef.current?.querySelector('.MuiOutlinedInput-root');

    if (root instanceof HTMLElement && root.offsetHeight > 0) {
      setPickerSize(root.offsetHeight);
    }
  }, []);

  React.useLayoutEffect(() => {
    measureFieldHeight();
  }, [measureFieldHeight]);

  React.useEffect(() => {
    const root = fieldWrapperRef.current?.querySelector('.MuiOutlinedInput-root');

    if (!(root instanceof HTMLElement)) {
      return;
    }

    const observer = new ResizeObserver(() => {
      measureFieldHeight();
    });

    observer.observe(root);

    return () => {
      observer.disconnect();
    };
  }, [measureFieldHeight]);

  React.useEffect(() => {
    const input = colorInputRef.current;

    if (!input || !onColorInputCommit) {
      return;
    }

    const handleCommit = (event: Event) => {
      const next = (event.target as HTMLInputElement).value.toLowerCase();
      lastValidColorRef.current = next;
      onColorInputCommit(next);
    };

    input.addEventListener('change', handleCommit);

    return () => {
      input.removeEventListener('change', handleCommit);
    };
  }, [onColorInputCommit]);

  React.useEffect(() => {
    const normalized = normalizeColorToHex(value, '');

    if (normalized && isValidHexColor(normalized)) {
      lastValidColorRef.current = normalized;
    }
  }, [value]);

  const pickerValue = resolvePickerColor(value, lastValidColorRef.current);
  const displayValue = normalizeColorToHex(value, '') || value;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap, minWidth }}>
      <Box
        component="input"
        type="color"
        ref={colorInputRef}
        value={pickerValue}
        disabled={disabled}
        onChange={e => {
          const next = e.target.value;
          lastValidColorRef.current = next.toLowerCase();
          (onPickerChange ?? onChange)(next);
        }}
        onMouseDown={e => {
          e.stopPropagation();
        }}
        sx={{
          width: pickerSize,
          height: pickerSize,
          minWidth: pickerSize,
          minHeight: pickerSize,
          p: 0,
          m: 0,
          border: 0,
          borderRadius: 1,
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          flexShrink: 0,
          appearance: 'none',
          bgcolor: 'transparent',
          boxSizing: 'border-box',
          '&::-webkit-color-swatch-wrapper': {
            p: 0,
            width: '100%',
            height: '100%',
          },
          '&::-webkit-color-swatch': {
            border: 0,
            borderRadius: 1,
            width: '100%',
            height: '100%',
          },
          '&::-moz-color-swatch': {
            border: 0,
            borderRadius: 1,
            width: '100%',
            height: '100%',
          },
        }}
      />
      <Box ref={fieldWrapperRef} sx={{ flex: 1 }}>
        <TextField
          inputRef={hexInputRef}
          placeholder="#000000"
          value={displayValue}
          onChange={e => {
            const next = e.target.value;

            if (isPartialHexColor(next)) {
              onChange(next);
            }
          }}
          onKeyDown={onHexKeyDown}
          onBlur={onHexBlur}
          disabled={disabled}
          error={hasError}
          inputProps={{ maxLength: 7 }}
          size="small"
          fullWidth
          sx={{
            ...(hasError !== undefined ? colorFieldSx(!!hasError) : {}),
          }}
        />
      </Box>
    </Box>
  );
};

export const ColorInlineEditor = ({
  value,
  onChange,
  onCommit,
  onCancel,
  disabled,
  hasError,
}: InlineEditorProps<string>) => {
  const draftColorRef = React.useRef(normalizeColorToHex(value || '', '') || value || '');

  React.useEffect(() => {
    draftColorRef.current = normalizeColorToHex(value || '', '') || value || '';
  }, [value]);

  const commitIfValid = (next: string) => {
    const normalized = normalizeColorToHex(next, '');

    if (!normalized || !isValidHexColor(normalized)) {
      return;
    }

    onChange(normalized);
    onCommit(normalized);
  };

  const displayValue = normalizeColorToHex(value || '', '') || value || '';

  return (
    <ColorField
      value={displayValue}
      onChange={next => {
        draftColorRef.current = next;
        onChange(next);
      }}
      onPickerChange={next => {
        const normalized = normalizeColorToHex(next, '');

        if (!normalized) {
          return;
        }

        draftColorRef.current = normalized;
        onChange(normalized);
      }}
      onColorInputCommit={next => {
        commitIfValid(next);
      }}
      disabled={disabled}
      hasError={hasError}
      gap={1}
      minWidth={220}
      onHexKeyDown={e => {
        if (e.key === 'Enter') {
          e.stopPropagation();
          e.preventDefault();
          commitIfValid(draftColorRef.current);
        } else if (e.key === 'Escape') {
          e.stopPropagation();
          e.preventDefault();
          onCancel();
        }
      }}
    />
  );
};

export default ColorField;
